import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  PencilSquareIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { Button, Pill, ToolTip } from "@truevault/aquilo";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";
import { useActionRequest } from "../../../common/hooks/api";
import { Api } from "../../../common/service/Api";
import { OrganizationPublicId } from "../../../common/service/server/Types";
import { Error as RequestsError } from "../../copy/requests";
import { useEditMode } from "../../edit-mode/EditModeContext";
import { clearEditModeFlag, getRemainingEditModeMs } from "../../edit-mode/editModeState";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { Routes } from "../../root/routes";

export const GLOBAL_ADMIN_EDIT_BAR_HEIGHT_PX = 64;
export const GLOBAL_ADMIN_EDIT_BAR_ID = "global-admin-edit-bar";
const EDIT_BAR_COLLAPSED_STORAGE_KEY = "global-admin-edit-bar-collapsed";

type GlobalAdminEditBarProps = {
  organizationId: OrganizationPublicId;
  pathname: string;
};

export const GlobalAdminEditBar = ({ organizationId, pathname }: GlobalAdminEditBarProps) => {
  const { isEditMode, requestEditMode, setIsEditMode } = useEditMode();
  const history = useHistory();
  const [storedIsCollapsed, setStoredIsCollapsed] = useLocalStorage(
    EDIT_BAR_COLLAPSED_STORAGE_KEY,
    "false",
  );
  const isCollapsed = storedIsCollapsed === "true";
  const [remainingEditModeMs, setRemainingEditModeMs] = useState(getRemainingEditModeMs);

  useEffect(() => {
    if (!isEditMode) {
      setRemainingEditModeMs(0);
      return;
    }

    const updateRemainingEditModeMs = () => setRemainingEditModeMs(getRemainingEditModeMs());

    updateRemainingEditModeMs();
    const interval = window.setInterval(updateRemainingEditModeMs, 1000);

    return () => window.clearInterval(interval);
  }, [isEditMode]);

  const exitEditMode = () => {
    clearEditModeFlag();
    setIsEditMode(false);
    window.location.reload();
  };

  const showSkipStep =
    isEditMode && pathname === Routes.getCompliant.privacyCenter.domainSetup && organizationId;

  const { fetch: skipDomainSetupStep, request: skipDomainSetupStepRequest } = useActionRequest({
    api: () => Api.consumerGroups.createDefaultCollectionGroups(organizationId),
    messages: { forceError: RequestsError.UpdateCollectionGroup },
    onSuccess: () => {
      history.push(Routes.getCompliant.businessSurveyAquilo);
    },
  });

  const modePill = (
    <Pill variant={isEditMode ? "warning" : "completed"} noMargin>
      {isEditMode ? "Edit mode" : "Read-only mode"}
    </Pill>
  );

  return ReactDOM.createPortal(
    <div
      id={GLOBAL_ADMIN_EDIT_BAR_ID}
      className="tw-boundary"
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: isCollapsed ? "auto" : 0,
        width: isCollapsed ? "20vw" : "auto",
        zIndex: 100,
      }}
    >
      <div className="h-[64px] bg-white border border-gray-300 shadow-[0_-8px_24px_rgb(15_23_42_/_0.14)] px-6 py-4 flex items-center gap-2 overflow-hidden">
        {isCollapsed ? (
          <>
            <button
              type="button"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Expand global admin edit bar"
              title="Expand edit bar"
              onClick={() => setStoredIsCollapsed("false")}
            >
              <ArrowsPointingOutIcon className="h-4 w-4" />
            </button>
            {modePill}
          </>
        ) : (
          <>
            <button
              type="button"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Collapse global admin edit bar"
              title="Collapse edit bar"
              onClick={() => setStoredIsCollapsed("true")}
            >
              <ArrowsPointingInIcon className="h-4 w-4" />
            </button>
            <Pill variant="primary" noMargin>
              Global Admin
            </Pill>
            {modePill}
            <div className="ml-auto flex items-center gap-2" data-edit-mode-read-only-allow>
              {showSkipStep && (
                <Button
                  color="tertiary"
                  size="s"
                  disabled={skipDomainSetupStepRequest.running}
                  onClick={skipDomainSetupStep}
                >
                  Skip Step
                </Button>
              )}
              {isEditMode ? (
                <ToolTip
                  text={`Time remaining in edit mode: ${formatEditModeTimeRemaining(
                    remainingEditModeMs,
                  )}`}
                  width="250px"
                >
                  <span>
                    <Button color="tertiary" size="s" onClick={exitEditMode}>
                      <span className="flex items-center gap-2">
                        <XMarkIcon className="h-4 w-4" />
                        Exit Edit Mode
                      </span>
                    </Button>
                  </span>
                </ToolTip>
              ) : (
                <Button color="primary" size="s" onClick={requestEditMode}>
                  <span className="flex items-center gap-2">
                    <PencilSquareIcon className="h-4 w-4" />
                    Edit
                  </span>
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

const formatEditModeTimeRemaining = (remainingMs: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${formatTimeUnit(hours, "hour")} ${formatTimeUnit(minutes, "minute")} ${formatTimeUnit(
      seconds,
      "second",
    )}`;
  }

  if (minutes === 0) {
    return formatTimeUnit(seconds, "second");
  }

  return `${formatTimeUnit(minutes, "minute")} ${formatTimeUnit(seconds, "second")}`;
};

const formatTimeUnit = (value: number, unit: "hour" | "minute" | "second") => {
  return `${value} ${unit}${value === 1 ? "" : "s"}`;
};
